import { Component, ElementRef } from '@angular/core';

import { ComponentsService } from '../../services/components.service';
import { AttributeDataService } from '../../services/attribute-data.service';
import { FeaturesService } from 'src/app/components/plans/features.service';
import { BrandingService } from '../services/branding.service';
import { UserComponentsService } from '../services/user-components.service';
import { LogoImageService } from '../services/logo-image.service';
import { StorageManagerService } from 'src/app/storage/services/storage-manager.service';
import { FileMetadataUtilsService } from '../services/file-metadata-utils.service';
import { BroadcasterService } from 'src/app/shared/services/broadcaster.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'template-component-branding',
  templateUrl: './branding.component.html',
  styleUrls: ['./branding.component.scss'],
  standalone: false,
})
export class BrandingComponent {
  public componentId: string;
  public defaultType: string;
  public type: string;
  public timezoneType: string;
  public timezone: string;
  public timeFormat: string;
  public dateFormat: string;

  brandingAvailable = this.featuresService.isFeatureAvailable(
    FeaturesService.FEATURE_BRANDING,
  );
  brandColorsExpanded = false;
  isLogoUploading = false;
  logoUploadList: File[] = [];
  private preservedCustomColors: { baseColor?: string; accentColor?: string };
  logoUploadManager = {
    onUploadStatus: (isUploading: boolean) => {
      this.isLogoUploading = isUploading;
    },
    addFile: (file: File) => {
      this.updateLogoMetadata([file]);
    },
    isSingleFileSelector: () => true,
  };

  constructor(
    private elementRef: ElementRef,
    private componentsFactory: ComponentsService,
    private attributeDataFactory: AttributeDataService,
    private featuresService: FeaturesService,
    private userComponentsService: UserComponentsService,
    private logoImageService: LogoImageService,
    private storageManagerService: StorageManagerService,
    private fileMetadataUtilsService: FileMetadataUtilsService,
    private broadcaster: BroadcasterService,
    public brandingFactory: BrandingService,
  ) {
    this.componentsFactory.registerDirective({
      type: 'rise-branding',
      element: this.elementRef.nativeElement,
    });
  }

  get canEditCompanyBranding() {
    return this.brandingAvailable && this.brandingFactory.canEditBranding();
  }

  get canOverrideBranding() {
    return !!this.brandingAvailable;
  }

  editLogo() {
    if (this.canEditCompanyBranding) {
      this.componentsFactory.editComponent({
        type: 'rise-image-logo',
      });
    }
  }

  addLogoToPresentation() {
    if (this.brandingAvailable) {
      const metadata = this.logoImageService.getImagesAsMetadata();
      const logo = metadata[0];

      if (!logo?.file) {
        return;
      }

      this.userComponentsService.addUserComponentsMediaItems(
        [
          {
            attributes: {
              editorLabel: 'Logo',
              environment: environment.production ? 'prod' : 'test',
              isLogo: true,
              source: logo.file,
              type: 'storage-file',
            },
          },
        ],
        false,
        'image',
      );
    }
  }

  activateLogoUploader(event?: Event) {
    event?.stopPropagation();

    if (!this.canEditCompanyBranding || this.isLogoUploading) {
      return;
    }

    const uploader = document.getElementById(
      'brand-logo-uploader',
    ) as HTMLInputElement;
    uploader?.click();
  }

  selectLogoFromStorage(event?: Event) {
    event?.stopPropagation();

    if (!this.canEditCompanyBranding || this.isLogoUploading) {
      return;
    }

    this.storageManagerService.fileType = StorageManagerService.FILE_TYPE.IMAGE;
    this.storageManagerService.isSingleFileSelector = () => true;
    this.storageManagerService.onSelectHandler = (newSelectedItems) => {
      this.updateLogoMetadata(newSelectedItems, true);
    };

    this.componentsFactory.editComponent({
      type: 'rise-storage-selector',
    });
  }

  removeLogo(event?: Event) {
    event?.stopPropagation();

    const logoMetadata = this.logoImageService.getImagesAsMetadata();

    if (!this.canEditCompanyBranding || !logoMetadata.length) {
      return;
    }

    return this.logoImageService
      .removeImage(logoMetadata[0], logoMetadata)
      .then((metadata: any[]) => {
        if (!metadata?.length) {
          return this.brandingFactory.saveBranding(true);
        }
      });
  }

  editColors() {
    if (this.canEditCompanyBranding) {
      this.enableBrandColors();
      this.brandColorsExpanded = true;
    }
  }

  get brandingSettings() {
    if (!this.brandingFactory.brandingSettings) {
      this.brandingFactory.brandingSettings = {};
    }

    return this.brandingFactory.brandingSettings;
  }

  get logoMetadata() {
    return this.logoImageService.getImagesAsMetadata();
  }

  get hasLogoSet() {
    return this.logoMetadata.some((logo) => !!logo?.file);
  }

  get logoThumbnailUrl() {
    return this.logoMetadata[0]?.['thumbnail-url'];
  }

  get customColors() {
    return (
      this.attributeDataFactory.getAttributeDataGlobal('brandingOverride') ||
      this.preservedCustomColors ||
      this.defaultCustomColors()
    );
  }

  isBrandColorsEnabled() {
    return !this.isCustomColorsEnabled();
  }

  isCustomColorsEnabled() {
    return !!this.attributeDataFactory.getAttributeDataGlobal(
      'brandingOverride',
    );
  }

  toggleBrandColors(enabled: boolean) {
    if (!this.canOverrideBranding) {
      return;
    }

    if (enabled) {
      this.enableBrandColors();
      this.brandColorsExpanded = this.canEditCompanyBranding;
    } else {
      this.enableCustomColors();
    }
  }

  toggleCustomColors(enabled: boolean) {
    if (!this.canOverrideBranding) {
      return;
    }

    if (enabled) {
      this.enableCustomColors();
    } else {
      this.enableBrandColors();
    }
  }

  saveBranding() {
    if (!this.canEditCompanyBranding) {
      return;
    }

    this.brandingFactory.setUnsavedChanges();
  }

  saveBrandColor(colorKey: 'baseColor' | 'accentColor', color: string) {
    if (!this.canEditCompanyBranding) {
      return;
    }

    this.brandingSettings[colorKey] = color;
    this.saveBranding();
  }

  saveCustomColor(colorKey: 'baseColor' | 'accentColor', color: string) {
    if (!this.canOverrideBranding) {
      return;
    }

    const customColors = {
      ...this.customColors,
      [colorKey]: color,
    };

    this.preservedCustomColors = customColors;
    this.attributeDataFactory.setAttributeDataGlobal('brandingOverride', {
      baseColor: customColors.baseColor,
      accentColor: customColors.accentColor,
    });
    this.emitPresentationUnsavedChanges();
  }

  closeBrandColorsEditor() {
    this.brandColorsExpanded = false;
  }

  showUpgradePlanModal() {
    this.featuresService.showUpgradePlanModal(FeaturesService.FEATURE_BRANDING);
  }

  private enableBrandColors() {
    const customColors =
      this.attributeDataFactory.getAttributeDataGlobal('brandingOverride');
    if (customColors) {
      this.preservedCustomColors = customColors;
    }

    this.attributeDataFactory.setAttributeDataGlobal('brandingOverride', null);
    this.emitPresentationUnsavedChanges();
  }

  private enableCustomColors() {
    this.brandColorsExpanded = false;
    const customColors = this.customColors;

    this.preservedCustomColors = customColors;
    this.attributeDataFactory.setAttributeDataGlobal(
      'brandingOverride',
      customColors,
    );
    this.emitPresentationUnsavedChanges();
  }

  private defaultCustomColors() {
    return {
      baseColor: this.brandingSettings.baseColor,
      accentColor: this.brandingSettings.accentColor,
    };
  }

  private emitPresentationUnsavedChanges() {
    this.broadcaster.emit(
      'risevision.template-editor.presentationUnsavedChanges',
    );
  }

  private updateLogoMetadata(files: any[], alwaysAppend = false) {
    const metadata = this.fileMetadataUtilsService.metadataWithFile(
      [],
      LogoImageService.DEFAULT_IMAGE_THUMBNAIL,
      files,
      alwaysAppend,
    );

    this.logoImageService.updateMetadata(metadata);
    if (metadata?.length) {
      this.brandingFactory.saveBranding(true);
    }
  }
}
